<script setup lang="ts">
import { toRefs, computed } from "vue";
import BaseDisplayRule from "@/modules/rule_engine/components/BaseDisplayRule.vue";
import { ConditionRule } from "@/modules/rule_engine/ruleEngineClasses";
import DisplayConditions from "@/modules/rule_engine/components/conditions/DisplayConditions.vue";
import DisplayActions from "@/modules/rule_engine/components/DisplayActions.vue";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import { displayColorFor } from "@/modules/rule_engine/ruleTypeColors";

const emit = defineEmits(["edit-rule", "delete-rule", "clone-rule"]);

const props = withDefaults(
	defineProps<{
		rule: ConditionRule;
		index: number;
		readOnly: boolean;
		checkable: boolean;
	}>(),
	{
		checkable: true,
	}
);

defineOptions({ name: "DisplayConditionRule" });

const { rule, readOnly, checkable } = toRefs(props);

const componentIsSelected = computed(() => {
	return useRuleEnginedStore().ruleEngineState.selectedRules.includes(
		rule.value.uuid
	);
});

const editRule = () => {
	emit("edit-rule");
};

const cloneRule = () => {
	emit("clone-rule");
};

const deleteRule = () => {
	emit("delete-rule");
};
</script>

<template>
	<base-display-rule
		:color-string="
			!componentIsSelected ? displayColorFor(rule.type) : 'lime-lighten-5'
		"
	>
		<template v-if="!readOnly && checkable" v-slot:prepend>
			<v-checkbox
				color="primary"
				hide-details
				v-model="useRuleEnginedStore().ruleEngineState.selectedRules"
				:value="rule.uuid"
			/>
		</template>
		<template v-slot:display>
			<display-conditions :conditions="rule.conditions" />
			<display-actions :actions="rule.actions" />
		</template>
		<template v-if="!readOnly" v-slot:actions>
			<v-btn
				@click="editRule"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="primary"
				v-tooltip="`Edit`"
			>
				<v-icon size="16">fa: fa-light fa-pen-to-square</v-icon>
			</v-btn>
			<v-btn
				@click="cloneRule"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="primary"
				v-tooltip="`Clone`"
			>
				<v-icon size="16">fa: fa-light fa-clone</v-icon>
			</v-btn>
			<v-btn
				@click="deleteRule"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="error"
				v-tooltip="`Delete`"
			>
				<v-icon size="16">fa: fa-light fa-trash-can</v-icon>
			</v-btn>
		</template>
	</base-display-rule>
</template>

<style scoped lang="scss"></style>
